import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { 
    ArrowLeft, Send, Sparkles, RotateCcw, 
    Loader2, MessageCircle, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssistantPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { organization } = useOrganization();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const userType = user?.user_type || 'residence';
    const isResident = userType === 'residence' || userType === 'residence_member';
    
    // Determine organization-specific labels
    const isBurston = organization?.slug === 'burston' || organization?.name?.toLowerCase().includes('burston');
    const isElysian = organization?.slug === 'elysian' || organization?.name?.toLowerCase().includes('elysian');
    
    // Get appropriate title and subtitle based on user type and organization
    const getTitle = () => {
        if (!isResident) return 'Ask Canopy';
        if (isBurston) return 'Garden Guide';
        return 'Concierge';
    };
    
    const getSubtitle = () => {
        if (!isResident) return 'Your gardening companion';
        if (isBurston) return 'Your garden companion';
        return 'Your digital assistant';
    };

    useEffect(() => {
        fetchSuggestions();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchSuggestions = async () => {
        try {
            const token = localStorage.getItem('burston_token');
            const res = await axios.get(`${API}/assistant/suggestions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuggestions(res.data.suggestions || []);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        }
    };

    const sendMessage = async (text = input) => {
        if (!text.trim() || loading) return;

        const userMessage = { role: 'user', content: text };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const token = localStorage.getItem('burston_token');
            const res = await axios.post(`${API}/assistant/chat`, {
                message: text,
                session_id: sessionId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSessionId(res.data.session_id);
            const assistantMessage = { role: 'assistant', content: res.data.response };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = { 
                role: 'assistant', 
                content: "I'm sorry, I'm having trouble responding right now. Please try again in a moment.",
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const clearSession = async () => {
        if (sessionId) {
            try {
                const token = localStorage.getItem('burston_token');
                await axios.post(`${API}/assistant/clear-session?session_id=${sessionId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Error clearing session:', error);
            }
        }
        setMessages([]);
        setSessionId(null);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-sand-dark/30 flex flex-col" data-testid="assistant-page">
            {/* Header */}
            <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-border/50 px-4 py-3">
                <div className="flex items-center justify-between max-w-2xl mx-auto">
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 rounded-full hover:bg-sand-dark/50 transition-colors"
                            data-testid="back-button"
                        >
                            <ArrowLeft className="w-5 h-5 text-foreground/70" />
                        </button>
                        <div>
                            <h1 className="text-lg font-serif text-foreground">
                                {getTitle()}
                            </h1>
                            <p className="text-xs text-muted-foreground">
                                {getSubtitle()}
                            </p>
                        </div>
                    </div>
                    {messages.length > 0 && (
                        <button
                            onClick={clearSession}
                            className="p-2 rounded-full hover:bg-sand-dark/50 transition-colors text-muted-foreground"
                            title="Start new conversation"
                            data-testid="clear-session-button"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </header>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
                <div className="max-w-2xl mx-auto space-y-4">
                    {messages.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                                <Sparkles className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="text-xl font-serif text-foreground mb-2">
                                {isResident ? 'How can I help you today?' : 'What can I help you with?'}
                            </h2>
                            <p className="text-muted-foreground text-sm mb-8 max-w-md mx-auto">
                                {isResident 
                                    ? 'Ask me about events, dining, services, or anything about your residence.'
                                    : 'Ask me about gardening, plants, workshops, or your membership.'}
                            </p>
                            
                            {/* Suggestion Pills */}
                            <div className="flex flex-wrap justify-center gap-2">
                                {suggestions.map((suggestion, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => sendMessage(suggestion)}
                                        className="px-4 py-2 bg-white rounded-full text-sm text-foreground/80 
                                                   border border-border/50 hover:border-primary/50 hover:bg-primary/5
                                                   transition-all duration-200 shadow-sm"
                                        data-testid={`suggestion-${idx}`}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((message, idx) => (
                                <div
                                    key={idx}
                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div className={`flex items-start gap-3 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                                            ${message.role === 'user' 
                                                ? 'bg-primary text-white' 
                                                : 'bg-sand-dark text-primary'}`}
                                        >
                                            {message.role === 'user' 
                                                ? <User className="w-4 h-4" />
                                                : <Sparkles className="w-4 h-4" />}
                                        </div>
                                        <div className={`px-4 py-3 rounded-2xl ${
                                            message.role === 'user'
                                                ? 'bg-primary text-white rounded-tr-md'
                                                : message.isError
                                                    ? 'bg-red-50 text-red-800 border border-red-200 rounded-tl-md'
                                                    : 'bg-white border border-border/50 text-foreground rounded-tl-md shadow-sm'
                                        }`}>
                                            <p className="text-sm whitespace-pre-wrap leading-relaxed">
                                                {message.content}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-sand-dark flex items-center justify-center">
                                            <Sparkles className="w-4 h-4 text-primary" />
                                        </div>
                                        <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-white border border-border/50 shadow-sm">
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span className="text-sm">Thinking...</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input Area */}
            <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-border/50 p-4">
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-end gap-3">
                        <div className="flex-1 relative">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={isBurston ? "Ask your garden guide..." : (isResident ? "Ask your concierge..." : "Ask about gardening...")}
                                rows={1}
                                className="w-full px-4 py-3 pr-12 rounded-2xl border border-border/50 
                                           focus:border-primary/50 focus:ring-2 focus:ring-primary/20
                                           resize-none text-sm bg-sand-dark/30 placeholder:text-muted-foreground/60
                                           transition-all duration-200"
                                style={{ minHeight: '48px', maxHeight: '120px' }}
                                disabled={loading}
                                data-testid="chat-input"
                            />
                        </div>
                        <button
                            onClick={() => sendMessage()}
                            disabled={!input.trim() || loading}
                            className="p-3 rounded-full bg-primary text-white disabled:opacity-50 
                                       disabled:cursor-not-allowed hover:bg-primary/90 transition-colors
                                       shadow-md hover:shadow-lg"
                            data-testid="send-button"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Send className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-center text-muted-foreground/60 mt-2">
                        Powered by Canopy AI
                    </p>
                </div>
            </div>
        </div>
    );
}
